import { RefObject } from 'react';

import { PresentationCoverSlideConfigBodyTextAlignment } from '@/types/schema';

import { CoverSlideConfigBodyTextAlignment } from '../shared/bodyTextAlignmentConfig';
import { CoverSlideConfigTitleTextSize } from '../shared/titleTextSizeConfig';

export { CoverSlideConfigBodyTextAlignment, CoverSlideConfigTitleTextSize };

export enum SlideConfigVariant {
  COVER = 'cover',
  DIVIDER = 'divider',
}

/**
 * Refs to slide internals exposed for the admin branding preview's
 * hover-halo wiring (T1-5984). When the user mouses over a drag
 * overlay, `SlideOriginDragOverlay` imperatively outlines the
 * matching ref so the indicator wraps the actual visible content.
 *
 * `titleHaloTarget` is the single halo target for the text drag
 * overlay. When the admin has the default-logo-position checkbox on,
 * the slide pads `titleHaloTarget`'s box upward (via negative margin
 * + positive padding) by the scaled-logo extension so a single
 * outline covers both the logo and the title content. When off, the
 * inline logo isn't inside the halo target — the custom-positioned
 * logo gets its own halo via `customLogo`.
 *
 * Outside of the admin preview this prop is omitted and slide
 * rendering is unaffected.
 */
export interface SlideHoverHaloRefs {
  /** Halo target around the title text + body content (and the inline logo when default-position is on). */
  titleHaloTarget?: RefObject<HTMLDivElement | null>;
  /** Custom-positioned logo wrapper when default position is off. */
  customLogo?: RefObject<HTMLDivElement | null>;
}

/**
 * Style overrides threaded into slide text editors (title + RTE) when
 * a tenant-level design config is active.
 *
 * `textColor` is applied to the slide title; `bodyTextColor` is applied
 * to the rich text body content.
 */
export interface TenantSlideStyle {
  textColor?: string | null;
  titleTextSize?: CoverSlideConfigTitleTextSize | null;
  /**
   * Continuous title size override in rem (0.1–5.5, T1-5972). When set,
   * overrides `titleTextSize` at render time. When null/undefined the
   * renderer falls back to the legacy `titleTextSize` lookup.
   */
  titleSizeRem?: number | null;
  bodyTextColor?: string | null;
  /**
   * Body text alignment applied to the rich text markdown content. When
   * null/undefined the body stays left-aligned (the default).
   */
  bodyTextAlignment?: PresentationCoverSlideConfigBodyTextAlignment | null;
}

/**
 * Full state for the cover/divider slide config, mirroring the backend
 * PresentationCoverSlideConfig entity. All fields are optional so the
 * slide can render with defaults when no config exists.
 *
 * TODO(T1-5645): Remove this interface once the slide components consume
 * the generated GraphQL fragment type directly.
 */
export interface PresentationCoverSlideConfig {
  backgroundColor?: string | null;
  useDarkLogo?: boolean | null;
  footerColor?: string | null;
  backgroundGradientEndColor?: string | null;
  backgroundGradientAngle?: number | null;
  backgroundOverlayPath?: string | null;
  backgroundOverlayAlpha?: number | null;
  backgroundOverlayPercentScale?: number | null;
  /** Overlay center coordinates as percentages of the slide bounds. */
  backgroundOverlayOriginX?: number | null;
  backgroundOverlayOriginY?: number | null;
  textColor?: string | null;
  backgroundImagePath?: string | null;
  backgroundImageAlpha?: number | null;
  titleTextSize?: CoverSlideConfigTitleTextSize | null;
  /**
   * Continuous title size in rem (0.1–5.5, T1-5972). Overrides
   * `titleTextSize` at render time when set; otherwise the slide falls
   * back to the legacy size lookup.
   */
  titleSizeRem?: number | null;
  bodyTextColor?: string | null;
  /**
   * Body text alignment for the rich text markdown content. Null/undefined
   * leaves the body left-aligned (the default).
   */
  bodyTextAlignment?: PresentationCoverSlideConfigBodyTextAlignment | null;
  includeFooter?: boolean | null;
  /**
   * Single inherit flag for divider slides. When true, the divider
   * uses every visual config value from the cover slide instead of
   * its own.
   */
  dividerInheritCover?: boolean | null;
  /**
   * Drag-and-drop origin offsets (T1-5959), in slide-coordinate pixels.
   * Each axis defaults to 0; positive values shift right/down. Background
   * origins offset from the centered cover position; text origins offset
   * from the title block's natural top-left.
   */
  backgroundImageOriginX?: number | null;
  backgroundImageOriginY?: number | null;
  textOriginX?: number | null;
  textOriginY?: number | null;
  /**
   * Width of the title/body text block, in slide-coordinate pixels.
   * Null/undefined stretches the block to the opposite slide padding.
   */
  textWidth?: number | null;
  /**
   * Scale applied to the background image, expressed as a 50–150
   * percentage (T1-5959). 100 (or null/undefined) renders the image at
   * the existing cover size; >100 enlarges and is cropped by the slide
   * bounds; <100 shrinks and letterboxes against the background color.
   */
  backgroundImagePercentScale?: number | null;
  /**
   * Continuous logo scale (1–200, T1-5959), replacing the legacy
   * 5-bucket logoSize enum. 100 (or null/undefined) renders identically
   * to the prior `Medium` size.
   */
  logoPercentScale?: number | null;
  /**
   * When true (or null/undefined — the default), the logo is anchored
   * to the title block. When false, the logo renders independently at
   * `(logoOriginX, logoOriginY)` and the title block lays out as
   * if no logo were present.
   */
  useDefaultLogoPosition?: boolean | null;
  /**
   * Drag-driven logo offsets in slide-coordinate pixels, applied as a
   * `transform: translate(...)` from the logo's natural origin. Only
   * consumed when `useDefaultLogoPosition` is false.
   */
  logoOriginX?: number | null;
  logoOriginY?: number | null;
  /**
   * When true, the logo is omitted from the slide entirely (both the
   * inline title-block slot and the custom-positioned variant). When
   * false / null / undefined, the logo renders per `useDefaultLogoPosition`.
   */
  hideLogo?: boolean | null;
}
